// ============================================================
// cargaDeTreino.js — a carga de cada semana do microciclo
//
// A planilha tem uma carga base e uma progressão em porcentagem por
// semana. As quatro semanas saem daí, e é conta, não campo: guardadas
// prontas, continuariam dizendo o valor antigo depois de alguém corrigir
// a base, e ninguém confere quatro números depois de mexer num quinto.
//
// ── A semana digitada vira âncora ───────────────────────────
//
// A progressão é previsão. Nem todo atleta sobe carga na semana prevista,
// e quando não sobe, o preparador escreve o que aconteceu.
//
// Antes, a semana digitada corrigia só aquela célula: escrever 42 kg na
// semana 2 deixava a 3 e a 4 ainda calculadas a partir da BASE, como se a
// correção não tivesse existido. O papel saía com um degrau no meio.
//
// Agora a semana digitada é o novo ponto de partida: dela para a frente a
// porcentagem é aplicada sobre o que foi registrado. As semanas
// ANTERIORES não mudam, porque já aconteceram.
//
// Texto que não é número ("peso do corpo", "elástico médio") aparece como
// está e não vira âncora: não há o que progredir sobre ele.
//
// Mora num arquivo só porque a tela e o PDF precisam dar o mesmo número.
// Eram duas contas em dois lugares, e a do PDF já divergia: ela corrigia
// a célula e seguia calculando da base.
// ============================================================

export const SEMANAS = 4;

const NUMERO = /-?\d+(\.\d+)?/;

// O número dentro do que a pessoa escreveu, e a unidade que sobrou.
//
// A coluna aceita texto porque "40 kg", "peso do corpo" e "elástico médio"
// são respostas igualmente legítimas. Quando há número, ele progride;
// quando não há, a linha fica com o texto, que é honesto.
export function lerCarga(valor) {
  const bruto = String(valor === undefined || valor === null ? "" : valor).replace(",", ".");
  const achado = bruto.match(NUMERO);
  if (!achado) return { numero: null, unidade: "" };
  const numero = Number(achado[0]);
  if (!Number.isFinite(numero) || numero <= 0) return { numero: null, unidade: "" };
  const unidade = bruto.slice(bruto.indexOf(achado[0]) + achado[0].length).trim().slice(0, 8);
  return { numero, unidade };
}

function formatar(numero, unidade) {
  const arredondado = Math.round(numero * 10) / 10;
  return String(arredondado).replace(".", ",") + (unidade ? " " + unidade : "");
}

// As quatro semanas, já com as correções aplicadas e a progressão seguindo
// de onde a última correção parou.
//
// Devolve sempre quatro posições. Linha sem carga nenhuma e sem correção
// devolve quatro vazios, e quem desenha decide se mostra traço.
export function semanasDaLinha({ carga, progressao, semanas } = {}) {
  const digitadas = Array.isArray(semanas) ? semanas : [];
  const base = lerCarga(carga);
  const passo = Number(String(progressao === undefined || progressao === null ? "" : progressao).replace(",", ".")) || 0;

  let ancora = base.numero === null ? null : { valor: base.numero, semana: 0, unidade: base.unidade };

  return Array.from({ length: SEMANAS }, (_, i) => {
    const escrito = String(digitadas[i] === undefined || digitadas[i] === null ? "" : digitadas[i]).trim();

    if (escrito) {
      const lido = lerCarga(escrito);
      // Só vira âncora o que tem número. "Elástico médio" na semana 2 não
      // dá o que progredir na 3, e a progressão continua do último número.
      if (lido.numero !== null) {
        ancora = { valor: lido.numero, semana: i, unidade: lido.unidade || base.unidade };
      }
      return escrito;
    }

    if (!ancora) return "";
    const fator = Math.pow(1 + passo / 100, i - ancora.semana);
    return formatar(ancora.valor * fator, ancora.unidade);
  });
}

// Só o que a conta produz, sem as correções. Serve de texto de apoio no
// campo vazio, para quem digita ver o que sairia se não escrevesse nada.
export function semanasDaCarga(carga, progressao) {
  const { numero } = lerCarga(carga);
  if (numero === null) return [];
  return semanasDaLinha({ carga, progressao, semanas: [] });
}
